import React from 'react';

/**
 * Error Boundary specifically for Firestore internal assertion errors
 * These are known harmless errors that occur during development
 */
class FirestoreErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a Firestore internal assertion error
    const errorMessage = error?.message || '';
    const isFirestoreInternalError = 
      errorMessage.includes('FIRESTORE') && 
      errorMessage.includes('INTERNAL ASSERTION');
    
    if (isFirestoreInternalError) {
      // Suppress Firestore internal errors - they're harmless
      console.warn('⚠️ Firestore internal assertion error caught (suppressed):', error);
      return { hasError: false, error: null }; // Don't show error UI
    }
    
    // For other errors, let them bubble up
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Check if this is a Firestore internal assertion error
    const errorMessage = error?.message || '';
    const isFirestoreInternalError = 
      errorMessage.includes('FIRESTORE') && 
      errorMessage.includes('INTERNAL ASSERTION');
    
    if (isFirestoreInternalError) {
      // Suppress - these are harmless during development
      console.warn('⚠️ Firestore internal assertion error (suppressed):', error);
      return;
    }
    
    // Log other errors normally
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    // If it's a Firestore internal error, don't show error UI
    if (this.state.hasError && this.state.error) {
      const errorMessage = this.state.error?.message || '';
      const isFirestoreInternalError = 
        errorMessage.includes('FIRESTORE') && 
        errorMessage.includes('INTERNAL ASSERTION');
      
      if (isFirestoreInternalError) {
        // Suppress the error and continue rendering
        return this.props.children;
      }
      
      // For other errors, show error UI
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h2>Something went wrong</h2>
          <p>Please refresh the page.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FirestoreErrorBoundary;
